<template>
  <!-- 主登录底栏：钱包选择器打开时完全隐藏，避免与 Web3Modal 叠层遮挡 -->
  <view
    v-if="visible && !walletPickerOpen && !sheetSuppressed"
    class="login-modal-overlay"
    @click="handleClose"
    @touchmove.stop.prevent
  >
    <view class="login-modal-content" @click.stop @touchmove.stop>
      <view class="sheet-close" @click="handleClose">
        <image class="sheet-close-icon" src="/static/image/lanhu/modal/close.webp" mode="aspectFit" />
      </view>
      <view class="modal-title">{{ t('loginModal.title') }}</view>

      <view
        class="connect-wallet-btn"
        :class="{ connecting: isConnecting, disabled: walletStore.isAuthenticated }"
        @click="handleConnectWallet"
      >
        <text class="btn-text">
          {{
            walletStore.isAuthenticated
              ? t('loginModal.alreadyConnected')
              : isConnecting
                ? t('loginModal.connecting')
                : t('loginModal.connectWallet')
          }}
        </text>
        <view class="btn-arrow" :class="{ loading: isConnecting }">
          <decision-icon
            v-if="!isConnecting && walletStore.isAuthenticated"
            type="yes"
            color="#171b24"
            :size="22"
          />
          <text v-else-if="!isConnecting && !walletStore.isAuthenticated">→</text>
          <view v-else class="loading-spinner"></view>
        </view>
      </view>
    </view>
  </view>

  <!--
    单一底部引导卡：合并「等待授权」+「打开钱包」，避免截图里上下两个弹层抢焦点。
    两步登录（1 连接 / 2 签名）用步骤条说清当前该做什么。
  -->
  <view
    v-if="showStatusBar"
    class="login-status-bar"
    @touchmove.stop.prevent
  >
    <view class="status-inner">
      <view class="status-head">
        <view class="status-steps" aria-hidden="true">
          <view class="status-step" :class="{ active: loginStep >= 1, done: loginStep > 1 }">
            <text>1</text>
          </view>
          <view class="status-step-line" :class="{ active: loginStep > 1 }" />
          <view class="status-step" :class="{ active: loginStep >= 2, done: loginStep > 2 }">
            <text>2</text>
          </view>
        </view>
        <view class="status-cancel" @click.stop="cancelPendingConnect">
          <text>{{ t('loginModal.cancel') }}</text>
        </view>
      </view>

      <view class="status-body">
        <view v-if="!networkRequired && loginStep < 3" class="status-spinner" />
        <view class="status-texts">
          <text class="status-eyebrow">{{ t('loginModal.loginStepLabel', { step: Math.min(loginStep, 2) }) }}</text>
          <text class="status-title">{{ statusTitle }}</text>
          <text class="status-desc">{{ statusDesc }}</text>
        </view>
      </view>

      <view v-if="networkRequired" class="status-cta status-cta-btn" @click.stop="handleConnectWallet">
        <text>{{ t('loginModal.retryLogin') }}</text>
      </view>
      <!-- 同步手势打开已配对钱包；唯一主按钮，不再叠顶部第二条 -->
      <a
        v-else-if="mobileWallet"
        class="status-cta status-cta-link"
        :href="mobileWallet.href"
        target="_self"
        rel="noreferrer"
        @click="onOpenWalletLinkClick"
      >
        {{ openWalletCtaText }}
      </a>
    </view>
  </view>

  <view v-if="showConfirmModal" class="login-modal-overlay" @click="cancelConfirm" @touchmove.stop.prevent>
    <view class="login-modal-content confirm-modal" @click.stop @touchmove.stop>
      <view class="sheet-close" @click="cancelConfirm">
        <image class="sheet-close-icon" src="/static/image/lanhu/modal/close.webp" mode="aspectFit" />
      </view>
      <view class="modal-title">{{ t('loginModal.confirmTitle') }}</view>

      <view class="confirm-warning">
        <text class="warning-text">{{ t('loginModal.confirmWarning') }}</text>
      </view>

      <view class="address-display">
        <text class="address-label">{{ t('loginModal.addressLabel') }}</text>
        <view class="address-box">
          <text class="address-text">{{ manualAddress }}</text>
        </view>
      </view>

      <view class="confirm-buttons">
        <button
          class="cancel-btn"
          :disabled="isBinding"
          @click="cancelConfirm"
        >
          {{ t('loginModal.cancel') }}
        </button>
        <button
          class="confirm-btn"
          :class="{ loading: isBinding }"
          :disabled="isBinding"
          @click="confirmBind"
        >
          <view v-if="isBinding" class="loading-spinner"></view>
          <text>{{ isBinding ? t('loginModal.binding') : t('loginModal.confirm') }}</text>
        </button>
      </view>
    </view>
  </view>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useWalletStore,
  isLoginModalPending,
  clearLoginModalPending,
} from '@/store/wallet';
import { useConfigStore } from '@/store/config';
import { useLoginUiStore } from '@/store/loginUi';
import {
  getWalletService,
  getWalletServiceIfLoaded,
} from '@/common/services/getWalletService';
import { storeToRefs } from 'pinia';
import { bindUserAddress } from '@/api/user';
import DecisionIcon from '@/components/decision-icon/index.vue';
import { useScrollLock } from '@/composables/useScrollLock';
import { useHideTabbarOnOverlay } from '@/composables/useHideTabbarOnOverlay';

const { t } = useI18n();
const walletStore = useWalletStore();
const configStore = useConfigStore();
const loginUi = useLoginUiStore();
const { openSeq, closeSeq, authCode: loginUiAuthCode } = storeToRefs(loginUi);

const visible = ref(false);
const showManualInput = ref(false);
const manualAddress = ref('');
const isBinding = ref(false);
const showConfirmModal = ref(false);
const isConnecting = ref(false);
const currentAuthCode = ref<string | null>(null);
/** Web3Modal / 钱包选择器是否打开 */
const walletPickerOpen = ref(false);
/** 主动避让：打开钱包选择器前隐藏登录底栏 */
const sheetSuppressed = ref(false);
const mobileWallet = ref<{ name: string; href: string } | null>(null);
const networkRequired = computed(() => walletStore.loginStage === 'network_required');

/**
 * 打开外部钱包：统一走 openMobileWallet（等桥短时就绪 + H5 保底）。
 * 一律 preventDefault，避免 WebView 把 okex:// 当资源加载报「不支持的URL」。
 */
const onOpenWalletLinkClick = async (e: Event) => {
  e.preventDefault();
  const walletService = await getWalletService();
  const latest = walletService.getMobileWalletLaunch();
  if (!latest?.href) {
    mobileWallet.value = null;
    return;
  }
  mobileWallet.value = latest;
  const ok = await walletService.openMobileWallet();
  if (!ok) mobileWallet.value = null;
};

/** 登录底栏 / 确认框锁滚动；状态引导卡一并藏 tabbar，避免与底栏叠层 */
const showStatusBar = computed(
  () =>
    !walletStore.isAuthenticated &&
    !walletPickerOpen.value &&
    (networkRequired.value ||
      isConnecting.value ||
      sheetSuppressed.value ||
      isLoginModalPending() ||
      !!mobileWallet.value)
);

const loginOverlayOpen = computed(
  () =>
    (visible.value && !walletPickerOpen.value && !sheetSuppressed.value) ||
    showConfirmModal.value
);
useScrollLock(loginOverlayOpen);
useHideTabbarOnOverlay(
  computed(() => loginOverlayOpen.value || showStatusBar.value)
);

/** 1 连接 · 2 签名 · 3 验证中（步骤点仍停在 2） */
const loginStep = computed(() => {
  const stage = walletStore.loginStage;
  if (stage === 'verify') return 3;
  if (stage === 'signature' || stage === 'network' || stage === 'network_required') return 2;
  return 1;
});

const statusTitle = computed(() => {
  if (networkRequired.value) return t('loginModal.networkRequired');
  if (walletStore.loginStage === 'network') return t('loginModal.switchingNetwork');
  if (walletStore.loginStage === 'signature') return t('loginModal.stepSignTitle');
  if (walletStore.loginStage === 'verify') return t('loginModal.verifyingLogin');
  return t('loginModal.stepConnectTitle');
});

const statusDesc = computed(() => {
  if (networkRequired.value) {
    return t('loginModal.networkRequiredHint', { chain: walletStore.loginNetworkName });
  }
  if (walletStore.loginStage === 'network') {
    return t('loginModal.switchingNetworkHint', { chain: walletStore.loginNetworkName });
  }
  if (walletStore.loginStage === 'signature') {
    return mobileWallet.value
      ? t('loginModal.stepSignHintWithOpen')
      : t('loginModal.stepSignHint');
  }
  if (walletStore.loginStage === 'verify') return t('loginModal.verifyingHint');
  if (mobileWallet.value) return t('loginModal.stepConnectHintWithOpen');
  return t('loginModal.stepConnectHint');
});

const openWalletCtaText = computed(() => {
  const wallet = mobileWallet.value?.name || '';
  if (walletStore.loginStage === 'signature' || walletStore.loginStage === 'network') {
    return t('loginModal.openWalletSign', { wallet });
  }
  return t('loginModal.openWalletConnect', { wallet });
});

watch(networkRequired, required => {
  if (!required) return;
  isConnecting.value = false;
  sheetSuppressed.value = true;
  clearLoginModalPending();
});

watch(
  () => walletStore.isAuthenticated,
  (newVal) => {
    if (newVal) {
      clearLoginModalPending();
      isConnecting.value = false;
      sheetSuppressed.value = false;
      walletPickerOpen.value = false;
      if (visible.value) {
        setTimeout(() => {
          visible.value = false;
          showManualInput.value = false;
          manualAddress.value = '';
          isConnecting.value = false;
          try {
            walletStore.emitBindInviteCheck(400);
          } catch {
            // ignore
          }
        }, 500);
      }
    }
  }
);

let unsubWalletModal: (() => void) | null = null;
let unsubMobileWalletLaunch: (() => void) | null = null;

const bindWalletModalListener = async () => {
  try {
    unsubWalletModal?.();
    const walletService = await getWalletService();
    unsubWalletModal = walletService.subscribeModalOpen((open) => {
      walletPickerOpen.value = open;
      if (open) {
        // 钱包弹层出现：立刻藏登录底栏，解除滚动锁冲突
        sheetSuppressed.value = true;
      } else if (!walletStore.isAuthenticated) {
        // 用户关掉钱包选择器且尚未登录：若仍有 pending 则保持状态条；否则恢复登录底栏
        if (isLoginModalPending() || isConnecting.value) {
          sheetSuppressed.value = true;
        } else {
          sheetSuppressed.value = false;
          if (visible.value) {
            // 恢复可点的登录底栏
          }
        }
      }
    });
  } catch (e) {
    console.warn('[LoginModal] subscribeModalOpen failed', e);
  }
};

/** Bind WC modal / deep-link listeners only when login UI actually needs web3 */
const ensureWalletUiListeners = async () => {
  await bindWalletModalListener();
  if (unsubMobileWalletLaunch) return;
  try {
    const walletService = await getWalletService();
    unsubMobileWalletLaunch = walletService.subscribeMobileWalletLaunch((link) => {
      mobileWallet.value = walletStore.isAuthenticated ? null : link;
    });
  } catch {
    mobileWallet.value = null;
  }
};

const open = (authCode?: string) => {
  if (walletStore.isAuthenticated && walletStore.address) {
    return;
  }

  if (!authCode) {
    try {
      const storedAuthCode = localStorage.getItem('authCode');
      if (storedAuthCode) authCode = storedAuthCode;
    } catch {
      // ignore
    }
  }

  currentAuthCode.value = authCode || null;
  if (authCode) {
    try {
      walletStore.setReferrerCode(authCode);
      localStorage.setItem('authCode', authCode);
    } catch {
      // ignore
    }
  }
  visible.value = true;
  showManualInput.value = false;
  manualAddress.value = '';
  // 若正在等钱包回跳，保持避让状态，只显示状态条
  if (isLoginModalPending() && !walletStore.isAuthenticated) {
    sheetSuppressed.value = true;
    isConnecting.value = true;
  } else {
    sheetSuppressed.value = false;
  }
  void ensureWalletUiListeners();
};

watch(
  openSeq,
  (seq) => {
    if (seq > 0) open(loginUiAuthCode.value);
  },
  { immediate: true }
);

const onOpenLoginEvent = (data?: { authCode?: string }) => {
  open(data?.authCode);
};

const resumeAfterWalletReturn = async (reason: string) => {
  if (walletStore.isAuthenticated) {
    clearLoginModalPending();
    sheetSuppressed.value = false;
    return;
  }
  if (!isLoginModalPending()) return;

  // 回跳续签：不弹出底栏挡住签名，只显示状态条
  visible.value = true;
  sheetSuppressed.value = true;
  isConnecting.value = true;
  void ensureWalletUiListeners();
  try {
    const ok = await walletStore.resumePendingWalletLogin(`modal-${reason}`);
    if (ok || walletStore.isAuthenticated) {
      clearLoginModalPending();
      sheetSuppressed.value = false;
    }
  } finally {
    if (!walletStore.isAuthenticated) {
      isConnecting.value = false;
    }
  }
};

const onVisibilityForWalletReturn = () => {
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
  // 回前台只静默续签，不再自动跳钱包；用户点底部「打开钱包」再主动唤起
  void resumeAfterWalletReturn('visibility');
};

onMounted(() => {
  // 布局常挂 login-modal：勿在 mount 时拉 web3；等 open / pending 续登再绑
  uni.$on('openLoginModal', onOpenLoginEvent);
  uni.$on('closeLoginModal', onCloseLoginEvent);
  void resumeAfterWalletReturn('mount');
  if (isLoginModalPending()) {
    void ensureWalletUiListeners();
    // walletService 可能稍晚才 init 完成，短延迟再绑一次
    setTimeout(() => {
      void bindWalletModalListener();
    }, 800);
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityForWalletReturn);
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('pageshow', onVisibilityForWalletReturn);
    window.addEventListener('focus', onVisibilityForWalletReturn);
  }
});

onUnmounted(() => {
  unsubMobileWalletLaunch?.();
  unsubMobileWalletLaunch = null;
  uni.$off('openLoginModal', onOpenLoginEvent);
  uni.$off('closeLoginModal', onCloseLoginEvent);
  unsubWalletModal?.();
  unsubWalletModal = null;
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityForWalletReturn);
  }
  if (typeof window !== 'undefined') {
    window.removeEventListener('pageshow', onVisibilityForWalletReturn);
    window.removeEventListener('focus', onVisibilityForWalletReturn);
  }
});

const pasteAddress = async () => {
  try {
    const res = await uni.getClipboardData();
    if (res.data) {
      manualAddress.value = res.data;
    }
  } catch (error) {
    console.error('[LoginModal] Paste error:', error);
  }
};

const handleClose = () => {
  visible.value = false;
  showManualInput.value = false;
  manualAddress.value = '';
  isConnecting.value = false;
  sheetSuppressed.value = false;
  mobileWallet.value = null;
  const loaded = getWalletServiceIfLoaded();
  if (loaded) {
    void loaded
      .then((walletService) => {
        try {
          walletService.closeModal();
        } catch {
          // ignore
        }
        try {
          if (!walletStore.isAuthenticated) {
            walletService.clearPersistedWalletChoice();
            walletStore.resetLoginAttempt();
          }
        } catch {
          // ignore
        }
      })
      .catch(() => {
        try {
          if (!walletStore.isAuthenticated) walletStore.resetLoginAttempt();
        } catch {
          // ignore
        }
      });
  } else if (!walletStore.isAuthenticated) {
    try {
      walletStore.resetLoginAttempt();
    } catch {
      // ignore
    }
  }
};

const onCloseLoginEvent = () => {
  handleClose();
};

watch(closeSeq, (seq) => {
  if (seq > 0) handleClose();
});

const cancelPendingConnect = () => {
  mobileWallet.value = null;
  isConnecting.value = false;
  sheetSuppressed.value = false;
  clearLoginModalPending();
  const loaded = getWalletServiceIfLoaded();
  if (loaded) {
    void loaded
      .then((walletService) => {
        try {
          walletService.closeModal();
        } catch {
          // ignore
        }
        // 取消后清掉「Open MetaMask」深链残留，下次仍进聚合钱包选择
        try {
          walletService.clearPersistedWalletChoice();
        } catch {
          // ignore
        }
      })
      .catch(() => undefined);
  }
  try {
    walletStore.resetLoginAttempt();
  } catch {
    // ignore
  }
  // 关掉引导卡后拉回登录底栏，方便重试
  if (!walletStore.isAuthenticated) {
    visible.value = true;
  }
};

const toggleManualInput = () => {
  showManualInput.value = !showManualInput.value;
};

const handleConnectWallet = async () => {
  if (walletStore.isAuthenticated) return;
  if (isConnecting.value) return;

  try {
    isConnecting.value = true;
    // 先收起登录底栏，再拉起钱包弹层，避免双层遮挡
    sheetSuppressed.value = true;
    // 勿在此处 resetLoginAttempt：会清掉登录意图，且清不了 WC 半截配对；
    // connect → openConnectModal 复用现有 SDK 和配对，禁止重建并发连接。

    let authTimer: ReturnType<typeof setTimeout> | null = null;
    const authPromise = new Promise<void>((resolve) => {
      if (walletStore.isAuthenticated) {
        resolve();
        return;
      }

      const unwatch = watch(
        () => walletStore.isAuthenticated || networkRequired.value,
        (finished) => {
          if (finished) {
            unwatch();
            if (authTimer) clearTimeout(authTimer);
            resolve();
          }
        }
      );

      authTimer = setTimeout(() => {
        unwatch();
        resolve();
      }, 180000);
    });

    try {
      await walletStore.connect();
    } catch (connectError: any) {
      isConnecting.value = false;
      if (networkRequired.value) {
        sheetSuppressed.value = true;
        return;
      }
      sheetSuppressed.value = false;
      try {
        walletStore.resetLoginAttempt();
      } catch {
        // ignore
      }

      const errorMsg = connectError?.message || '';
      const userRejected =
        errorMsg.includes('User rejected') ||
        errorMsg.includes('User denied') ||
        errorMsg.includes('user rejected');
      if (!userRejected && errorMsg && errorMsg !== 'NETWORK_SWITCH_REQUIRED') {
        uni.showToast({
          title: errorMsg.slice(0, 40) || t('loginModal.connectFailed'),
          icon: 'none',
        });
      }
      return;
    }

    await authPromise;

    if (walletStore.address && walletStore.isAuthenticated) {
      const inviteCode = currentAuthCode.value || walletStore.referrerCodeFromUrl || '';
      if (inviteCode) {
        try {
          walletStore.setReferrerCode(inviteCode);
        } catch {
          // ignore
        }
      }
      handleClose();
      walletStore.emitBindInviteCheck(500);
    } else if (!walletStore.isAuthenticated) {
      // 仍在跳转/授权：保持状态条，不弹全屏 toast 抢焦点
      sheetSuppressed.value = true;
    }
  } catch (error: any) {
    console.error('[LoginModal] Connect error:', error);
    sheetSuppressed.value = false;
  } finally {
    if (walletStore.isAuthenticated) {
      isConnecting.value = false;
    }
  }
};

const performBind = async (address: string, authCode: string) => {
  isBinding.value = true;
  try {
    const response = await bindUserAddress(address, authCode);

    if (response.access_token) {
      const { saveToken, setCurrentWalletAddress } = await import('@/common/services/request');
      setCurrentWalletAddress(address);
      saveToken(response.access_token, response.expires_in, address);
      walletStore.$patch({
        accessToken: response.access_token,
        address: address,
        isConnected: true,
      });
      await configStore.loadDictionary();
      await walletStore.fetchUserAssets(true);
      walletStore.emitBindInviteCheck(400);
    }

    currentAuthCode.value = null;
    try {
      localStorage.removeItem('authCode');
    } catch {
      // ignore
    }
  } catch (error: any) {
    console.error('[LoginModal] Bind failed:', error);
    throw error;
  } finally {
    isBinding.value = false;
  }
};

const isValidAddress = (address: string): boolean => {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
};

const handleManualBind = async () => {
  if (!manualAddress.value) {
    uni.showToast({ title: t('loginModal.inputPlaceholder'), icon: 'none' });
    return;
  }
  if (!isValidAddress(manualAddress.value)) {
    uni.showToast({ title: t('loginModal.invalidAddress'), icon: 'none' });
    return;
  }
  if (!currentAuthCode.value) {
    uni.showToast({ title: t('loginModal.noAuthCode'), icon: 'none' });
    return;
  }
  showConfirmModal.value = true;
};

const confirmBind = async () => {
  isBinding.value = true;
  try {
    if (!currentAuthCode.value) {
      throw new Error('AuthCode is required');
    }
    const response = await bindUserAddress(manualAddress.value, currentAuthCode.value);
    if (response.access_token) {
      const { saveToken, setCurrentWalletAddress } = await import('@/common/services/request');
      setCurrentWalletAddress(manualAddress.value);
      saveToken(response.access_token, response.expires_in, manualAddress.value);
      walletStore.$patch({
        accessToken: response.access_token,
        address: manualAddress.value,
        isConnected: true,
      });
      await configStore.loadDictionary();
      await walletStore.fetchUserAssets(true);
      walletStore.emitBindInviteCheck(400);
    }
    currentAuthCode.value = null;
    try {
      localStorage.removeItem('authCode');
    } catch {
      // ignore
    }
    showConfirmModal.value = false;
    uni.showToast({ title: t('loginModal.bindSuccess'), icon: 'success' });
    setTimeout(() => {
      handleClose();
      setTimeout(() => {
        uni.showToast({ title: t('loginModal.loginSuccess'), icon: 'success' });
      }, 500);
    }, 1000);
  } catch (error: any) {
    showConfirmModal.value = false;
    uni.showToast({ title: t('loginModal.bindFailed'), icon: 'none' });
  } finally {
    isBinding.value = false;
  }
};

const cancelConfirm = () => {
  showConfirmModal.value = false;
};

defineExpose({
  open,
  close: handleClose,
});
</script>

<style lang="scss" scoped>
@use '@/common/styles/sheet-modal.scss' as *;

.login-modal-overlay {
  @include lanhu-sheet-overlay;
  /* 低于 Web3Modal(--w3m-z-index: 100000)，高于普通业务弹层 */
  z-index: 20000;
  animation: lanhuSheetFadeIn 0.25s ease;
}

.login-modal-content {
  @include lanhu-sheet-panel;
  padding: 48rpx 34rpx;
  padding-bottom: calc(40rpx + env(safe-area-inset-bottom));
}

.sheet-close {
  position: absolute;
  right: 34rpx;
  top: 30rpx;
  z-index: 2;
  @include lanhu-close-btn;
}

.modal-title {
  @include lanhu-sheet-title;
  margin-bottom: 48rpx;
  padding: 0 80rpx;
}

.connect-wallet-btn {
  @include lanhu-primary-btn;
  width: 100%;
  justify-content: space-between;
  padding: 0 28rpx 0 40rpx;
  color: $lanhu-on-brand;

  &.connecting,
  &.disabled {
    opacity: 0.6;
    pointer-events: none;
  }

  &.disabled {
    background: #c6c6c6;
  }

  .btn-text {
    font-size: 28rpx;
    font-weight: 500;
    color: $lanhu-on-brand;
    line-height: 1;
  }

  .btn-arrow {
    width: 56rpx;
    height: 56rpx;
    background: #ffffff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32rpx;
    color: $lanhu-orange;
    line-height: 1;
    flex-shrink: 0;

    &.loading {
      animation: pulse 1.5s ease-in-out infinite;
    }
  }
}

/* 单一底部引导卡：白底 lanhu，步骤 + 唯一主 CTA */
.login-status-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 19990;
  pointer-events: none;
  display: flex;
  justify-content: center;
  padding: 0 24rpx calc(20rpx + env(safe-area-inset-bottom));
  box-sizing: border-box;
  animation: statusBarIn 0.28s ease;
}

.status-inner {
  pointer-events: auto;
  width: 100%;
  max-width: var(--app-max-width, 750px);
  display: flex;
  flex-direction: column;
  gap: 20rpx;
  padding: 24rpx;
  border-radius: 24rpx;
  background: #ffffff;
  box-shadow: 0 0 34rpx rgba(17, 24, 37, 0.14);
  border: 1rpx solid $lanhu-badge-bg;
  box-sizing: border-box;
}

.status-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16rpx;
}

.status-steps {
  display: flex;
  align-items: center;
  gap: 10rpx;
}

.status-step {
  width: 40rpx;
  height: 40rpx;
  border-radius: 50%;
  background: #f2f3f5;
  display: flex;
  align-items: center;
  justify-content: center;

  text {
    font-size: 22rpx;
    font-weight: 700;
    color: $lanhu-muted;
    line-height: 1;
  }

  &.active {
    background: $lanhu-orange;

    text {
      color: $lanhu-on-brand;
    }
  }

  &.done {
    background: $lanhu-badge-bg;

    text {
      color: $lanhu-badge-text;
    }
  }
}

.status-step-line {
  width: 36rpx;
  height: 4rpx;
  border-radius: 2rpx;
  background: #e8e9eb;

  &.active {
    background: $lanhu-orange;
  }
}

.status-body {
  display: flex;
  align-items: flex-start;
  gap: 16rpx;
}

.status-spinner {
  width: 36rpx;
  height: 36rpx;
  margin-top: 6rpx;
  border-radius: 50%;
  border: 3rpx solid rgba(186, 244, 0, 0.35);
  border-top-color: $lanhu-orange;
  flex-shrink: 0;
  animation: spin 0.8s linear infinite;
}

.status-texts {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6rpx;
}

.status-eyebrow {
  font-size: 22rpx;
  font-weight: 500;
  color: $lanhu-badge-text;
  line-height: 1.2;
}

.status-title {
  font-family: 'Noto Sans SC', sans-serif;
  font-size: 28rpx;
  font-weight: 700;
  color: $lanhu-title;
  line-height: 1.3;
}

.status-desc {
  font-family: 'Noto Sans SC', sans-serif;
  font-size: 22rpx;
  color: $lanhu-desc;
  line-height: 1.4;
}

.status-cancel {
  flex-shrink: 0;
  min-height: 56rpx;
  padding: 0 22rpx;
  border-radius: 28rpx;
  background: $lanhu-badge-bg;
  border: 1rpx solid $lanhu-badge-border;
  display: flex;
  align-items: center;
  justify-content: center;

  text {
    font-size: 24rpx;
    color: $lanhu-badge-text;
    font-weight: 500;
    line-height: 1;
  }

  &:active {
    opacity: 0.88;
  }
}

.status-cta {
  @include lanhu-primary-btn;
  width: 100%;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;

  text,
  & {
    color: $lanhu-on-brand;
    font-size: 28rpx;
    font-weight: 500;
  }
}

.status-cta-link {
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirm-warning {
  margin-bottom: 24rpx;

  .warning-text {
    font-size: 26rpx;
    color: #666;
    line-height: 1.5;
  }
}

.address-display {
  margin-bottom: 32rpx;

  .address-label {
    display: block;
    font-size: 24rpx;
    color: #999;
    margin-bottom: 12rpx;
  }

  .address-box {
    padding: 20rpx 24rpx;
    background: #f7f7f7;
    border-radius: 16rpx;

    .address-text {
      font-size: 24rpx;
      color: #171b24;
      word-break: break-all;
    }
  }
}

.confirm-buttons {
  display: flex;
  gap: 20rpx;

  button {
    flex: 1;
    height: 80rpx;
    border-radius: 40rpx;
    font-size: 28rpx;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .cancel-btn {
    background: #f0f0f0;
    color: #333;
  }

  .confirm-btn {
    background: #baf400;
    color: #171b24;
    font-weight: 600;

    &.loading {
      opacity: 0.7;
    }
  }
}

.loading-spinner {
  width: 32rpx;
  height: 32rpx;
  border: 3rpx solid #e0e0e0;
  border-top-color: #baf400;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes statusBarIn {
  from {
    opacity: 0;
    transform: translateY(24rpx);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
}
</style>
